import React from 'react';
import { Stack, ScrollArea } from '@mantine/core';
import { NavLink } from '@mantine/core';
import {
  IconDashboard,
  IconChartBar,
  IconMessageCircle,
  IconEye,
  IconFileText,
  IconBuilding,
  IconLink,
  IconMapPin,
  IconUsers,
  IconDatabaseImport,
  IconClipboardCheck,
  IconAddressBook,
  IconMailCheck,
  IconHeartHandshake,
  IconChartFunnel
} from '@tabler/icons-react';
import { useLocation, useNavigate } from 'react-router-dom';
import { ADMIN_ROUTES } from '../../routing/routes';

/**
 * AdminNavbar Component
 *
 * Full navigation sidebar for admin users with links to:
 * - Dashboard
 * - Analytics
 * - Mentions
 * - Mention Review
 * - Post Review
 * - Businesses
 * - Company Management
 * - Regions
 * - Users
 * - Data Imports
 * - Review Queue (SESSION_312: unified Alias + Address Corrections)
 * - CRM
 */
export const AdminNavbar: React.FC = (): React.ReactElement => {
  const location = useLocation();
  const navigate = useNavigate();

  const isActive = (path: string): boolean => {
    return location.pathname === path || location.pathname.startsWith(`${path}/`);
  };

  const handleNavigate = (path: string): void => {
    navigate(path);
  };

  return (
    <ScrollArea h="calc(100vh - 60px)">
    <Stack gap="xs" p="md">
      <NavLink
        label="Dashboard"
        leftSection={<IconDashboard size={20} stroke={1.5} />}
        active={isActive(ADMIN_ROUTES.DASHBOARD)}
        onClick={(): void => handleNavigate(ADMIN_ROUTES.DASHBOARD)}
        variant="subtle"
      />

      <NavLink
        label="Analytics"
        leftSection={<IconChartBar size={20} stroke={1.5} />}
        active={isActive(ADMIN_ROUTES.ANALYTICS)}
        onClick={(): void => handleNavigate(ADMIN_ROUTES.ANALYTICS)}
        variant="subtle"
      />

      <NavLink
        label="Mentions"
        leftSection={<IconMessageCircle size={20} stroke={1.5} />}
        active={isActive(ADMIN_ROUTES.MENTIONS)}
        onClick={(): void => handleNavigate(ADMIN_ROUTES.MENTIONS)}
        variant="subtle"
      />

      <NavLink
        label="Mention Review"
        leftSection={<IconEye size={20} stroke={1.5} />}
        active={isActive(ADMIN_ROUTES.MENTION_REVIEW)}
        onClick={(): void => handleNavigate(ADMIN_ROUTES.MENTION_REVIEW)}
        variant="subtle"
      />

      <NavLink
        label="Post Review"
        leftSection={<IconFileText size={20} stroke={1.5} />}
        active={isActive(ADMIN_ROUTES.POST_REVIEW)}
        onClick={(): void => handleNavigate(ADMIN_ROUTES.POST_REVIEW)}
        variant="subtle"
      />

      <NavLink
        label="Businesses"
        leftSection={<IconBuilding size={20} stroke={1.5} />}
        active={isActive(ADMIN_ROUTES.BUSINESSES)}
        onClick={(): void => handleNavigate(ADMIN_ROUTES.BUSINESSES)}
        variant="subtle"
      />

      <NavLink
        label="Company Management"
        leftSection={<IconLink size={20} stroke={1.5} />}
        active={isActive(ADMIN_ROUTES.COMPANIES)}
        onClick={(): void => handleNavigate(ADMIN_ROUTES.COMPANIES)}
        variant="subtle"
      />

      <NavLink
        label="Regions"
        leftSection={<IconMapPin size={20} stroke={1.5} />}
        active={isActive(ADMIN_ROUTES.REGIONS)}
        onClick={(): void => handleNavigate(ADMIN_ROUTES.REGIONS)}
        variant="subtle"
      />

      <NavLink
        label="Users"
        leftSection={<IconUsers size={20} stroke={1.5} />}
        active={isActive(ADMIN_ROUTES.USERS)}
        onClick={(): void => handleNavigate(ADMIN_ROUTES.USERS)}
        variant="subtle"
      />

      <NavLink
        label="Data Imports"
        leftSection={<IconDatabaseImport size={20} stroke={1.5} />}
        active={isActive(ADMIN_ROUTES.IMPORTS)}
        onClick={(): void => handleNavigate(ADMIN_ROUTES.IMPORTS)}
        variant="subtle"
      />

      <NavLink
        label="Non-Profits"
        leftSection={<IconHeartHandshake size={20} stroke={1.5} />}
        active={isActive(ADMIN_ROUTES.NONPROFITS)}
        onClick={(): void => handleNavigate(ADMIN_ROUTES.NONPROFITS)}
        variant="subtle"
      />

      <NavLink
        label="Review Queue"
        leftSection={<IconClipboardCheck size={20} stroke={1.5} />}
        active={isActive(ADMIN_ROUTES.ADMIN_REVIEW)}
        onClick={(): void => handleNavigate(ADMIN_ROUTES.ADMIN_REVIEW)}
        variant="subtle"
      />

      <NavLink
        label="CRM"
        leftSection={<IconAddressBook size={20} stroke={1.5} />}
        active={isActive(ADMIN_ROUTES.CRM_DASHBOARD) || isActive(ADMIN_ROUTES.CRM_CONTACTS)}
        onClick={(): void => handleNavigate(ADMIN_ROUTES.CRM_DASHBOARD)}
        variant="subtle"
      />

      <NavLink
        label="Email Analytics"
        leftSection={<IconMailCheck size={20} stroke={1.5} />}
        active={isActive(ADMIN_ROUTES.CRM_EMAIL_ANALYTICS)}
        onClick={(): void => handleNavigate(ADMIN_ROUTES.CRM_EMAIL_ANALYTICS)}
        variant="subtle"
      />

      <NavLink
        label="Funnel Tracking"
        leftSection={<IconChartFunnel size={20} stroke={1.5} />}
        active={isActive(ADMIN_ROUTES.FUNNEL)}
        onClick={(): void => handleNavigate(ADMIN_ROUTES.FUNNEL)}
        variant="subtle"
      />
    </Stack>
    </ScrollArea>
  );
};
